import React from 'react';
import { Upload } from 'lucide-react';
import { AppModal, AppModalHeader } from '../../../components/AppModal';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import type { OfferAttachment } from '../../../types';
import { uploadOfferAttachment } from '../utils/offerAttachmentsApi';
import { isInlineImageAttachment } from '../utils/offerDocumentContent';
import { OfferAttachmentDocumentPicker } from './OfferAttachmentDocumentPicker';
import { SortableOfferImageTile } from './SortableOfferImageTile';

type Props = {
  leadRouteId: string;
  remainingSlots: number;
  existingObjectPaths: ReadonlySet<string>;
  images?: OfferAttachment[];
  onImagesChange?: (next: OfferAttachment[]) => void | Promise<void>;
  onAdd: (added: OfferAttachment[]) => void | Promise<void>;
  onClose: () => void;
};

const ACCEPT = 'image/png,image/jpeg';

export function OfferImagePickerDialog({
  leadRouteId,
  remainingSlots,
  existingObjectPaths,
  images,
  onImagesChange,
  onAdd,
  onClose,
}: Props) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const editMode = Boolean(images && onImagesChange);
  const currentImages = images ?? [];
  const maxSelect = remainingSlots;

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || busy) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [busy, onClose]);

  const sortSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!onImagesChange) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = currentImages.findIndex((image) => image.id === active.id);
    const newIndex = currentImages.findIndex((image) => image.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    await onImagesChange(arrayMove(currentImages, oldIndex, newIndex));
  };

  const applyAdded = async (added: OfferAttachment[]) => {
    const next = added.slice(0, maxSelect);
    if (next.length === 0) return;
    if (editMode && onImagesChange) {
      await onImagesChange([...currentImages, ...next]);
      return;
    }
    await onAdd(next);
    onClose();
  };

  const uploadFiles = async (files: FileList | File[]) => {
    if (maxSelect <= 0) {
      setError('Es sind bereits 12 Bilder im Dokument. Bitte zuerst Bilder entfernen.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const added: OfferAttachment[] = [];
      for (const file of Array.from(files)) {
        if (added.length >= maxSelect) break;
        if (!isInlineImageAttachment({ mime_type: file.type })) {
          throw new Error('Nur PNG- und JPEG-Bilder können eingefügt werden.');
        }
        added.push(await uploadOfferAttachment(leadRouteId, file));
      }
      if (added.length > 0) await applyAdded(added);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Esc behandelt der Capture-Listener oben (vor dem Editor); AppModal nur für Rahmen und Klick daneben.
  return (
    <AppModal
      open
      onClose={onClose}
      disableClose={busy}
      overlayClassName="z-[260]"
      panelClassName="md:max-w-2xl"
      ariaLabelledBy="offer-image-picker-title"
    >
      <AppModalHeader
        titleId="offer-image-picker-title"
        title={editMode ? 'Bilder bearbeiten' : 'Bilder hinzufügen'}
        onClose={onClose}
        closeDisabled={busy}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-3">
        <p className="text-xs text-app-text-subtle">
          {editMode
            ? 'Reihenfolge per Ziehen ändern. Neue Bilder werden an diese Reihe angehängt.'
            : `Bilder erscheinen an der gewählten Stelle im Dokument (3 Spalten). Noch ${remainingSlots} von 12 Plätzen frei. PDFs und weitere Dateien bleiben Anhänge am Ende.`}
        </p>

        {editMode && currentImages.length > 0 ? (
          <DndContext sensors={sortSensors} collisionDetection={closestCenter} onDragEnd={(event) => void handleDragEnd(event)}>
            <SortableContext items={currentImages.map((image) => image.id)} strategy={rectSortingStrategy}>
              <ul className="grid grid-cols-3 gap-3">
                {currentImages.map((attachment) => (
                  <SortableOfferImageTile
                    key={attachment.id}
                    leadRouteId={leadRouteId}
                    attachment={attachment}
                    busy={busy}
                    onRemove={(image) => {
                      if (!onImagesChange) return;
                      void onImagesChange(currentImages.filter((item) => item.id !== image.id));
                    }}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        ) : null}

        <button
          type="button"
          disabled={busy || maxSelect <= 0}
          onClick={() => fileInputRef.current?.click()}
          className="w-full text-left rounded-xl border border-dashed border-sky-200 bg-app-surface-muted/60 px-4 py-5 hover:bg-sky-50/50 hover:border-sky-300 transition-colors disabled:opacity-60 disabled:pointer-events-none"
        >
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">
              <Upload className="w-5 h-5" />
            </div>
            <p className="text-sm text-app-text-muted">
              Bild hier auswählen oder{' '}
              <span className="text-sky-700 font-semibold underline underline-offset-2">Datei wählen</span>
            </p>
            <p className="text-xs text-app-text-subtle">
              Erlaubt: PNG, JPEG (Max. 25 MB pro Datei)
              {editMode ? ` · Noch ${remainingSlots} von 12 Plätzen frei` : ''}
            </p>
          </div>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={ACCEPT}
          onChange={(e) => {
            const fs = e.target.files;
            if (fs && fs.length > 0) void uploadFiles(fs);
          }}
        />

        <OfferAttachmentDocumentPicker
          leadRouteId={leadRouteId}
          existingObjectPaths={existingObjectPaths}
          imagesOnly
          maxImages={maxSelect}
          onAdd={async (added) => {
            await applyAdded(added);
          }}
        />

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : null}
      </div>
    </AppModal>
  );
}
